/**
 * NotaryPublicViewPage — Vue publique read-only pour le notaire (P2 batch 2).
 * Accessible via /notaire/partage/:token — pas d'auth.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, FileText, ShieldCheck, Loader2, AlertCircle } from 'lucide-react';
import api from '../../lib/api';

const CATEGORY_LABELS = {
    vendor: 'Vendeur',
    title: 'Titre & mandat',
    diagnostics: 'Diagnostics',
    copro: 'Copropriété',
    fiscal: 'Fiscal & urbanisme',
};

const STATUS_LABELS = {
    pending: 'Attendu', received: 'Reçu', validated: 'Validé', na: 'Non applicable',
};

export default function NotaryPublicViewPage() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/public/notary/dossiers/by-token/${token}`);
            setData(r.data);
        } catch (e) {
            if (e?.response?.status === 410) setError('Ce lien est expiré.');
            else if (e?.response?.status === 404) setError('Ce lien est invalide.');
            else setError('Impossible de charger le dossier.');
        } finally { setLoading(false); }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const downloadItem = async (itemId, filename) => {
        try {
            const r = await api.get(`/public/notary/dossiers/by-token/${token}/items/${itemId}/download`,
                                     { responseType: 'blob' });
            const url = window.URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url; a.download = filename || 'piece.pdf';
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) { /* silencieux */ }
    };

    return (
        <div style={{ minHeight: '100vh', background: '#f7f6f2', color: '#1a1224' }}
             data-testid="notary-public-view">
            <header className="border-b py-4 px-6"
                    style={{ background: '#fff', borderColor: 'rgba(0,0,0,0.06)' }}>
                <div className="mx-auto max-w-3xl flex items-center gap-3">
                    <ShieldCheck className="w-5 h-5" style={{ color: '#681793' }} />
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider"
                           style={{ color: '#681793' }}>
                            Dossier notaire · lecture seule
                        </p>
                        <p className="text-xs" style={{ color: 'rgba(0,0,0,0.55)' }}>
                            Partagé via Homly — l'agent reste responsable des pièces.
                        </p>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-4 sm:px-6 py-6 space-y-4">
                {loading && (
                    <div className="text-center py-10">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto"
                                  style={{ color: '#681793' }} />
                    </div>
                )}
                {error && (
                    <div className="p-4 rounded-xl flex items-start gap-3"
                         data-testid="public-view-error"
                         style={{ background: '#fff', border: '1px solid rgba(220,38,38,0.3)' }}>
                        <AlertCircle className="w-5 h-5 mt-0.5" style={{ color: '#dc2626' }} />
                        <div>
                            <p className="font-semibold">{error}</p>
                            <p className="text-xs mt-1" style={{ color: 'rgba(0,0,0,0.55)' }}>
                                Demandez à l'agent immobilier un nouveau lien de partage.
                            </p>
                        </div>
                    </div>
                )}
                {data && (
                    <>
                        <section className="p-5 rounded-2xl"
                                 style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.06)' }}>
                            <h1 className="text-xl font-bold">{data.dossier.seller_name}</h1>
                            <p className="text-sm mt-0.5" style={{ color: 'rgba(0,0,0,0.55)' }}>
                                {data.dossier.property_address}
                                {data.dossier.city ? ` · ${data.dossier.city}` : ''}
                                {data.dossier.postal_code ? ` ${data.dossier.postal_code}` : ''}
                            </p>
                            {data.dossier.target_signing_date && (
                                <p className="text-xs mt-2" style={{ color: 'rgba(0,0,0,0.55)' }}>
                                    Date cible de signature : {new Date(data.dossier.target_signing_date).toLocaleDateString('fr-FR')}
                                </p>
                            )}
                            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold"
                                 style={{ background: 'rgba(104,23,147,0.10)', color: '#681793' }}>
                                {data.progress.required_percent}% des pièces requises validées
                            </div>
                        </section>

                        {Object.entries(
                            data.items.reduce((acc, it) => {
                                (acc[it.category] = acc[it.category] || []).push(it); return acc;
                            }, {})
                        ).map(([cat, list]) => (
                            <section key={cat}
                                     data-testid={`public-category-${cat}`}
                                     className="space-y-2">
                                <h2 className="text-xs font-semibold uppercase tracking-wider"
                                    style={{ color: 'rgba(0,0,0,0.55)' }}>
                                    {CATEGORY_LABELS[cat] || cat}
                                </h2>
                                <ul className="space-y-1">
                                    {list.map((it) => (
                                        <li key={it.id}
                                            className="p-3 rounded-lg border flex items-center justify-between gap-3"
                                            data-testid={`public-item-${it.code}`}
                                            style={{ background: '#fff', borderColor: 'rgba(0,0,0,0.06)' }}>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium">{it.label}</p>
                                                <p className="text-[11px]"
                                                   style={{ color: 'rgba(0,0,0,0.5)' }}>
                                                    {STATUS_LABELS[it.status]}
                                                    {it.filename && ` · ${it.filename}`}
                                                </p>
                                            </div>
                                            {it.filename && (
                                                <button type="button"
                                                        onClick={() => downloadItem(it.id, it.filename)}
                                                        data-testid={`public-download-${it.code}`}
                                                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border"
                                                        style={{ borderColor: 'rgba(104,23,147,0.35)', color: '#681793' }}>
                                                    <Download className="w-3 h-3" /> Télécharger
                                                </button>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ))}
                    </>
                )}
            </main>
        </div>
    );
}
